import React, { useRef, useState } from "react";
import Slider from "react-slick";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../assets/css/BlogsFAQ.css";
import b1 from "../assets/img/blogs/b1.jpeg";
import b2 from "../assets/img/blogs/b2.webp";
import b3 from "../assets/img/blogs/b3.webp";
import b4 from "../assets/img/blogs/b4.jpeg";
import b5 from "../assets/img/blogs/b5.webp";
import b6 from "../assets/img/blogs/b6.webp";

const blogPosts = [
    {
        id: 1,
        featured: true,
        category: "Campus Life",
        date: "24 July 2026",
        readTime: "6 min read",
        title: "A Campus Designed to Inspire Learning, Discovery and Growth",
        excerpt:
            "Explore how thoughtfully designed classrooms, laboratories, libraries and collaborative spaces shape a rewarding university experience.",
        image: b1,
        link: "/blog/campus-designed-for-growth",
    },
    {
        id: 2,
        category: "Career Guidance",
        date: "18 July 2026",
        readTime: "5 min read",
        title: "How to Choose the Right University Programme for Your Career",
        excerpt:
            "A practical guide to comparing your interests, strengths, career goals and emerging industry opportunities.",
        image: b2,
        link: "/blog/choose-the-right-programme",
    },
    {
        id: 3,
        category: "Innovation",
        date: "12 July 2026",
        readTime: "4 min read",
        title: "Why Practical Learning Matters in Modern Higher Education",
        excerpt:
            "Laboratory work, projects and industry exposure help students connect academic concepts with real-world applications.",
        image: b3,
        link: "/blog/practical-learning",
    },
    {
        id: 4,
        category: "Student Success",
        date: "05 July 2026",
        readTime: "5 min read",
        title: "Building Skills Beyond the Classroom",
        excerpt:
            "Discover how clubs, events, presentations and teamwork contribute to confidence, communication and leadership.",
        image: b4,
        link: "/blog/skills-beyond-classroom",
    },
    {
        id: 5,
        category: "Research",
        date: "28 June 2026",
        readTime: "7 min read",
        title: "Creating a Culture of Research and Academic Curiosity",
        excerpt:
            "Learn how research-led education encourages inquiry, critical thinking, innovation and meaningful knowledge creation.",
        image: b5,
        link: "/blog/research-and-curiosity",
    },
    {
        id: 6,
        category: "Admissions",
        date: "20 June 2026",
        readTime: "4 min read",
        title: "Your University Admission Checklist",
        excerpt:
            "Keep your application process organised with a simple checklist covering eligibility, documents and important decisions.",
        image: b6,
        link: "/blog/admission-checklist",
    },
];

function BlogArrow({ direction, onClick }) {
    return (
        <button
            type="button"
            className={`blog-nav-arrow blog-nav-arrow--${direction}`}
            onClick={onClick}
            aria-label={`${direction === "prev" ? "Previous" : "Next"} blog posts`}
        >
            {direction === "prev" ? "←" : "→"}
        </button>
    );
}

function BlogCard({ post }) {
    return (
        <article className="university-blog-card">
            <a href={post.link} className="university-blog-card__image">
                <img src={post.image} alt={post.title} loading="lazy" />
                <span>{post.category}</span>
            </a>
            <div className="university-blog-card__content">
                <div className="university-blog-card__meta">
                    <time>{post.date}</time>
                    <i />
                    <span>{post.readTime}</span>
                </div>
                <h3>
                    <a href={post.link}>{post.title}</a>
                </h3>
                <p>{post.excerpt}</p>
                <a href={post.link} className="university-blog-card__link">
                    Read article <span>↗</span>
                </a>
            </div>
        </article>
    );
}

export default function Blog() {
    const blogSlider = useRef(null);
    const featured = blogPosts.find((post) => post.featured);
    const remainingPosts = blogPosts.filter((post) => !post.featured);

    const settings = {
        arrows: false,
        dots: false,
        infinite: false,
        speed: 600,
        slidesToShow: 3,
        slidesToScroll: 1,
        swipeToSlide: true,
        responsive: [
            {
                breakpoint: 1100,
                settings: { slidesToShow: 2.15 },
            },
            {
                breakpoint: 720,
                settings: { slidesToShow: 1.12 },
            },
        ],
    };

    return (
        <section className="university-blogs" aria-labelledby="blogs-heading">
            <div className="university-section-container">
                <header className="university-blogs__header">
                    <div>
                        <span className="university-section-eyebrow">
                            <i /> University perspectives
                        </span>
                        <h2 id="blogs-heading">
                            Ideas, experiences and
                            <em> stories that shape tomorrow.</em>
                        </h2>
                    </div>
                    <div className="university-blogs__header-side">
                        <p>
                            Explore campus stories, student guidance, academic insights and
                            ideas from across the university community.
                        </p>
                        <a href="/blogs">View all articles <span>↗</span></a>
                    </div>
                </header>

                <article className="university-featured-blog">
                    <a href={featured.link} className="university-featured-blog__image">
                        <img src={featured.image} alt={featured.title} />
                    </a>
                    <div className="university-featured-blog__content">
                        <div className="university-featured-blog__number">Featured · 01</div>
                        <span>{featured.category}</span>
                        <h3>{featured.title}</h3>
                        <p>{featured.excerpt}</p>
                        <div className="university-featured-blog__footer">
                            <div>
                                <time>{featured.date}</time>
                                <small>{featured.readTime}</small>
                            </div>
                            <a href={featured.link} aria-label={`Read ${featured.title}`}>
                                <span>Read story</span> ↗
                            </a>
                        </div>
                    </div>
                </article>

                <div className="university-blog-browser">
                    <div className="university-blog-browser__top">
                        <div>
                            <strong>Latest from the University</strong>
                            <span>{String(remainingPosts.length).padStart(2, "0")} articles</span>
                        </div>
                        <div className="university-blog-browser__controls">
                            <BlogArrow
                                direction="prev"
                                onClick={() => blogSlider.current?.slickPrev()}
                            />
                            <BlogArrow
                                direction="next"
                                onClick={() => blogSlider.current?.slickNext()}
                            />
                        </div>
                    </div>
                    <Slider
                        ref={blogSlider}
                        {...settings}
                        className="university-blog-slider"
                    >
                        {remainingPosts.map((post) => (
                            <div className="university-blog-slide" key={post.id}>
                                <BlogCard post={post} />
                            </div>
                        ))}
                    </Slider>
                </div>
            </div>
        </section>
    );
}
